import React, { useEffect, useMemo, useState, useRef, KeyboardEvent, ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import Select, { components } from "react-select";
import "datatables.net";
import "datatables.net-responsive";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import {
  DesktopTableLoader,
  MobileCardsLoader,
  DataTableStatus,
} from "components/DataTableLoader";
import { InstallationStatusBadge } from "components/Installationstatus";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";
import { formatPaymentMethodLabel } from "../../utils/paymentMethodLabel";



const DATE_OPTIONS = [
  { label: "Today", value: "today" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 15 Days", value: "last_15_days" },
  { label: "Last 30 Days", value: "last_30_days" },
  { label: "Custom", value: "custom" },
];

/** Placeholder option — full list is built in the component. */
const EMPTY_ORDER_STATUS_OPTION = { value: "", label: "Order Status" };

// const PRODUCTION_STATUS_OPTIONS = [
//   { value: "", label: "Production Status" },
//   { value: "Not Started", label: "Not Started" },
//   { value: "In Production", label: "In Production" },
//   { value: "Quality Check", label: "Quality Check" },
//   { value: "Ready for Installation", label: "Ready for Installation" },
// ];

const PAYMENT_STATUS_OPTIONS = [
  { value: "", label: "Payment Status" },
  { value: "Paid", label: "Paid" },
  { value: "Partial Payment", label: "Partial Payment" },
  { value: "Pending", label: "Pending" },
  { value: "Overdue", label: "Overdue" }
];

const INVOICE_STATUS_OPTIONS = [
  { value: "", label: "Invoice Status" },
  { value: "yes", label: "Invoice Sent" },
  { value: "no", label: "Not Sent" },
];

const PRIORITY_OPTIONS = [
  { value: "", label: "Priority" },
  { value: "1", label: "High" },
  { value: "2", label: "Medium" },
  { value: "3", label: "Low" }
];

const DropdownIndicator = (props: any) => {
  if (props.selectProps.isDisabled) return null;
  return (
    <components.DropdownIndicator {...props}>
      <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
    </components.DropdownIndicator>
  );
};

const customSelectStyles = {
  indicatorsContainer: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  dropdownIndicator: (p: any, s: any) => ({ ...p, display: s.isDisabled ? "none" : "flex" }),
  indicatorSeparator: () => ({ display: "none" }),
  control: (p: any, s: any) => ({
    ...p,
    backgroundColor: s.isDisabled ? "#f2f2f2" : p.backgroundColor,
    cursor: s.isDisabled ? "not-allowed" : "pointer",
    opacity: 1,
  }),
  singleValue: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  valueContainer: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  input: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color, WebkitTextFillColor: s.isDisabled ? "#000" : undefined }),
  placeholder: (p: any, s: any) => ({ ...p, color: s.isDisabled ? "#000" : p.color }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999, position: "absolute", top: "100%", left: 0, right: 0 }),
  menuList: (provided: any) => ({ ...provided, maxHeight: "220px" }),
};


export interface InstallationOrder {
  _id: string;
  order_number: string;
  created_at: string;
  status: number;
  brokerage_firm_name?: string;     // For firm name (optional)
  brokerage_firm_admin?: string;    // Fallback if name missing (optional)
  office?: {
    officeName: string;
    // other fields you may use later
  } | string | null;
  agent_name?: string;
  line_items: {
    production_status?: string;
    storage_locations?: {
      product_location_status?: string;
      // other fields
    }[];
    // other fields
  }[];
  installation_details?: {
    scheduled_date?: string;
    installer_name?: string;
    // other fields
  };
  payment_status?: string;
  payment_method?: string;
  priority?: number;  // number: 1,2,3
  project_id?: string;
  project_number?: string;
  // other fields you want to use
}


function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}


const STATUS_MAP: { [key: number]: string } = {
  100: "Draft",
  200: "Finalized",
  300: "Scheduled",
  400: "In Progress",
  500: "Completed",
  600: "Cancelled",
};

const columnHelper = createColumnHelper<InstallationOrder>();
const SignageJobsInstallations = () => {
  const [installationOrders, setInstallationOrders] = useState<any[]>([]);
  const [orders, setOrders] = useState<InstallationOrder[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const userName = getAuthCookie("userName");
  const location = useLocation();
  const navigate = useNavigate();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.installation_orders
  );

  function getInstallationWorkOrderId(order: InstallationOrder): string {
    return String((order as any)?._id ?? (order as any)?.id ?? (order as any)?.order_id ?? "");
  }

  // State for parent signage firm (for SignageTeammember login)
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);

  // signageId: parent signage firm when SignageTeammember, else userId
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : (userId || "");

  // Fetch parent hierarchy for SignageTeammember login
  useEffect(() => {
    const loadParentHierarchy = async () => {
      if (isSignageTeammember && token) {
        const hierarchyData = await fetchParentHierarchy(token);
        if (hierarchyData) {
          setParentSignageId(hierarchyData.parentSignageId);
          setParentSignageName(hierarchyData.parentSignageName);
        }
      }
    };
    loadParentHierarchy();
  }, [isSignageTeammember, token]);

  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredAgents, setFilteredAgents] = useState<any[]>([]);
  // const [brokerageFirms, setBrokerageFirms] = useState<BrokerageFirm[]>([]);
  const [brokerageFirmFilter, setBrokerageFirmFilter] = useState("");
  const [exporting, setExporting] = useState(false);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const topRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const hasShownToast = useRef(false);
  const fetchRequestIdRef = useRef(0);
  const hasLoadedDataRef = useRef(false);
  const [datePreset, setDatePreset] = useState(DATE_OPTIONS[3]);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const { minDate, maxDate } = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(now.getMonth() - 6);
    return {
      minDate: sixMonthsAgo.toISOString().split("T")[0],
      maxDate: now.toISOString().split("T")[0],
    };
  }, []);

  useEffect(() => {
    if (datePreset.value === "custom") {
      if (customFrom && customFrom < minDate) setCustomFrom("");
      if (customTo && customTo < minDate) setCustomTo("");
      if (customFrom && customFrom > maxDate) setCustomFrom("");
      if (customTo && customTo > maxDate) setCustomTo("");
    }
  }, [datePreset.value, minDate, maxDate]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [orderStatusFilter, setOrderStatusFilter] = useState(EMPTY_ORDER_STATUS_OPTION);
  const [invoiceSentFilter, setInvoiceSentFilter] = useState(INVOICE_STATUS_OPTIONS[0]);

  const orderStatusOptions = useMemo(
    () => [
      EMPTY_ORDER_STATUS_OPTION,
      { value: "100", label: "Draft" },
      { value: "200", label: "Finalized" },
      { value: "300", label: "Scheduled" },
      { value: "400", label: "In Progress" },
      { value: "500", label: "Completed" },
      { value: "600", label: "Cancelled" },
    ],
    []
  );

  useEffect(() => {
    setOrderStatusFilter((prev) => {
      if (prev.value !== "100") return prev;
      const next = orderStatusOptions.find((o) => o.value === "100");
      return next && next.label !== prev.label ? next : prev;
    });
  }, [orderStatusOptions]);

  // const [productionStatusFilter, setProductionStatusFilter] = useState(PRODUCTION_STATUS_OPTIONS[0]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState(PAYMENT_STATUS_OPTIONS[0]);
  const [priorityFilter, setPriorityFilter] = useState(PRIORITY_OPTIONS[0]);
  const [generateClicked, setGenerateClicked] = useState(false);



  // useEffect(() => {
  //   const fetchInstallationOrders = async () => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/installation_orders/signage/${userId}`,
  //         {
  //           method: "GET",
  //           headers: {
  //             "Content-Type": "application/json",
  //             Authorization: `Bearer ${token}`,
  //           },
  //         }
  //       );

  //       const data = await response.json();
  //       console.log("Installation Orders:", data);
  //       if (!response.ok) throw new Error(data.message || "Failed to fetch installation orders");

  //       setInstallationOrders(data);
  //     } catch (err: any) {
  //       console.error("Installation Orders Fetch Error:", err.message);
  //     }
  //   };

  //   fetchInstallationOrders();
  // }, [token]);

  useEffect(() => {
    if (isSignageTeammember && !parentSignageId) return;
    if (!signageId || !token) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    generateClicked,
    pagination.pageIndex,
    pagination.pageSize,
    globalFilter,
    signageId,
    token,
    isSignageTeammember,
    parentSignageId,
  ]);

  function handleGenerate() {
    setPagination({ ...pagination, pageIndex: 0 });
    setGenerateClicked(true);
  }


  // Fetch Data
  // useEffect(() => {
  const fetchOrders = async () => {
    const requestId = ++fetchRequestIdRef.current;
    const showInitialLoader = !hasLoadedDataRef.current;
    if (showInitialLoader) setLoading(true);
    const hasSearch = Boolean(globalFilter?.trim());
    const dateRange = hasSearch
      ? { type: "", from: "", to: "" }
      : {
          type: datePreset.value,
          from: datePreset.value === "custom" ? customFrom : "",
          to: datePreset.value === "custom" ? customTo : "",
        };
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/installation_orders/signage/${signageId}/datatable`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            page: pagination.pageIndex + 1,
            limit: pagination.pageSize,
            search: globalFilter,
            date_range: dateRange,
            filters: {
              invoice_sent: invoiceSentFilter.value || "",
              status: orderStatusFilter.value || undefined,
              // production_status: productionStatusFilter.value || undefined,
              payment_status: paymentStatusFilter.value === "all" ? undefined : paymentStatusFilter.value || undefined,
              priority: (priorityFilter.value || undefined)
            },
            sort: {
              field: "created_at",
              order: "desc",
            },
            // Add filters and date_range if needed
          })
        }
      );
      if (!res.ok) throw new Error("Access denied. You don’t have view permission for installation orders.");
      const data = await res.json();
      if (requestId !== fetchRequestIdRef.current) return;
      setOrders(data?.data || []);
      setRowCount(data?.total || 0);
      hasLoadedDataRef.current = true;
    } catch (err: any) {
      if (requestId !== fetchRequestIdRef.current) return;
      setOrders([]);
      setRowCount(0);
      showError(err?.message || "Failed to fetch installation orders");
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
        // Maintain cursor focus in search input after loading
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }
    }
  };

  //   fetchOrders();
  // }, [pagination, globalFilter, token, userId]);

  const handleExportCsv = async () => {
    if (!signageId || !token) return;
    try {
      setExporting(true);
      const url = `${process.env.REACT_APP_API_URL}/installation_orders/signage/${signageId}/export/csv`;
      const body = {
        page: 1,
        limit: rowCount || 10000,
        search: globalFilter ?? "",
        sort: {
          field: "created_at",
          order: "desc",
        },
        filters: {
          invoice_sent: invoiceSentFilter?.value ?? "",
          status: orderStatusFilter?.value ?? "",
          priority: priorityFilter?.value ?? "",
          payment_status: paymentStatusFilter?.value ?? "",
        },
        dateRange: {
          type: datePreset?.value ?? "",
          from: datePreset?.value === "custom" ? customFrom : "",
          to: datePreset?.value === "custom" ? customTo : "",
        },
      };
      const res = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "installation_orders.csv";
      link.click();
      URL.revokeObjectURL(link.href);
      showSuccess("Export downloaded");
    } catch (err: any) {
      showError(err?.message || "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (catId: string) => {
    setDeleting(catId);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/categories/${catId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete the category.");
      }
      showSuccess("Category deleted successfully!");
      // setCategories((prev) => prev.filter((category) => category.id !== catId));
      setDeleting(null);
      navigate(0);
    } catch (err) {
      showError("Failed to delete the category.");
      setError(
        err instanceof Error
          ? err.message
          : "An unknown error occurred while deleting."
      );
      setDeleting(null);
    }
  };

  const PRIORITY_LABELS: { [key: number]: string } = {
    1: "High",
    2: "Medium",
    3: "Low",
  };

  // Columns
  const columns = useMemo(() => [
    // columnHelper.display({
    //   id: "sno",
    //   header: () => <div className="th-text S-No">S.No.</div>,
    //   cell: (info) => (
    //     <div className="text-sm S-No">
    //       {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
    //     </div>
    //   ),
    // }),

     columnHelper.accessor("order_number", {
    id: "order_number",
    header: () => <div className="th-text S-No">Order ID</div>,
    cell: (info) => {
      const row = info.row.original;
      // pick whichever your data uses:
      const orderId =
        (row as any).id ?? (row as any)._id ?? (row as any).order_id;

      return (
        <Link
          to={`${baseRoute}/jobs/installationWorkOrder/${orderId}`}
          className="text-sm S-No order-link-1"
        >
          {info.getValue() ?? ""}
        </Link>
      );
    },
    filterFn: fuzzyTextFilter,
    enableGlobalFilter: true,
  }),
    // columnHelper.accessor("order_number", {
    //   header: () => <div className="th-text S-No">Order ID</div>,
    //   cell: (info) => <div className="text-sm S-No  order-link-1">{info.getValue()}</div>,
    // }),
    columnHelper.accessor("created_at", {
      header: () => <div className="th-text ">Created Date</div>,
      cell: (info) => (
        <div className="text-sm">
          {new Date(info.getValue()).toLocaleDateString()}
        </div>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        const pn = String((row as any).project_number ?? "").trim();
        return pn || "--";
      },
      {
        id: "project_number",
        header: () => (
          <div className="th-text">Project Id</div>
        ),
        cell: (info) => {
          const value = String(info.getValue() ?? "").trim();
          const row = info.row.original;
          const projectId = String(
            (row as any).project_id ?? (row as any).projectId ?? ""
          ).trim();
          const canOpenProject = Boolean(projectId && value && value !== "--");
          return (
            canOpenProject ? (
              <Link
                to={`${baseRoute}/projects/view/${encodeURIComponent(projectId)}`}
                className="text-sm order-link-1"
              >
                {value}
              </Link>
            ) : (
              <div className="text-sm">{value || "--"}</div>
            )
          );
        },
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),
    // Brokerage Firm
    columnHelper.accessor(row =>
      row.brokerage_firm_name || row.brokerage_firm_admin || "—",
      {
        id: "brokerage_firm_name",
        header: () => <div className="th-text">Brokerage Firm</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Office
    columnHelper.accessor(row =>
      (row.office && typeof row.office === "object" && row.office.officeName)
        ? row.office.officeName
        : (typeof row.office === "string" ? row.office : "—"),
      {
        id: "office",
        header: () => <div className="th-text">Office</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Agent
    columnHelper.accessor("agent_name", {
      header: () => <div className="th-text">Agent</div>,
      cell: info => <div className="text-sm">{info.getValue() || "—"}</div>,
    }),
    // Installer
    columnHelper.accessor(row =>
      row.installation_details?.installer_name || "—",
      {
        id: "installer_name",
        header: () => <div className="th-text">Installer</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
      }
    ),
    // Installation Date
    columnHelper.accessor(row => {
      const scheduledDate = row.installation_details?.scheduled_date;
      if (!scheduledDate || String(scheduledDate).startsWith("0001-01-01")) return "--";
      return new Date(scheduledDate).toLocaleDateString();
    }, {
      id: "installation_date",
      header: () => <div className="th-text">Installation Date</div>,
      cell: info => <div className="text-sm">{info.getValue()}</div>,
    }),

    // Payment Status column
    columnHelper.accessor("payment_status", {
      header: () => <div className="th-text">Payment Status</div>,
      cell: info => (
        <div className="text-sm">
          <InstallationStatusBadge value={info.getValue() as string} type="payment" />
        </div>
      ),
    }),
    columnHelper.accessor(
      (row) => formatPaymentMethodLabel(row.payment_method),
      {
        id: "payment_method",
        header: () => <div className="th-text">Payment Method</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
        filterFn: fuzzyTextFilter,
        enableGlobalFilter: true,
      }
    ),

    // Order Status column
    columnHelper.accessor("status", {
      header: () => <div className="th-text">Status</div>,
      cell: info => {
        const statusVal = info.getValue() as number;
        return (
          <div className="text-sm">
            <InstallationStatusBadge
              type="order"
              value={
                statusVal
              }
            />
          </div>
        );
      },
    }),
    // Priority column
    columnHelper.accessor(
      row => (row.priority !== undefined ? PRIORITY_LABELS[row.priority] : undefined),
      {
        id: "priority",
        header: () => <div className="th-text">Priority</div>,
        cell: info => (
          <div className="text-sm">
            <InstallationStatusBadge value={info.getValue() as string} type="priority" />
          </div>
        ),
      }
    ),

    // Payment Due Date (last payment_info object has the latest due_date)
    columnHelper.accessor(
      row => {
        const paymentStatus = String((row as any)?.payment_status || "").trim().toLowerCase();
        if (paymentStatus === "paid") return "--";

        const paymentInfo = (row as any)?.payment_info;
        const last = Array.isArray(paymentInfo) && paymentInfo.length > 0 ? paymentInfo[paymentInfo.length - 1] : null;
        const dueDate = last?.due_date;
        if (!dueDate || String(dueDate).startsWith("0001-01-01")) return "--";
        const d = new Date(dueDate);
        return isNaN(d.getTime())
          ? "--"
          : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      },
      {
        id: "payment_due_date",
        header: () => <div className="th-text">Payment Due Date</div>,
        cell: info => <div className="text-sm">{info.getValue()}</div>,
        enableGlobalFilter: false,
      }
    ),

    columnHelper.accessor(
      (row) => {
        const status = String((row as any)?.quote_status ?? "").trim().toLowerCase();
        if (!status) return "-";
        return status
          .split("_")
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(" ");
      },
      {
        id: "quote_status",
        header: () => <div className="th-text">Quote Status</div>,
        cell: (info) => <div className="text-sm">{info.getValue()}</div>,
        enableGlobalFilter: false,
      }
    ),

    columnHelper.accessor(
      (row) => {
        const raw = String((row as any)?.invoice_sent ?? "").trim().toLowerCase();
        return raw === "yes" || raw === "true" || raw === "1";
      },
      {
        id: "invoice_sent",
        header: () => <div className="th-text">Invoice Sent</div>,
        cell: (info) => {
          const sent = info.getValue<boolean>();
          return (
            <div className="text-sm">
              {sent ? (
                <input
                  type="checkbox"
                  checked
                  disabled
                  readOnly
                  className="form-check-input invoice-sent-checkbox border-success"
                  style={{
                    width: "1.2rem",
                    height: "1.2rem",
                    cursor: "not-allowed",
                  }}
                  title="Yes"
                />
              ) : (
                "-"
              )}
            </div>
          );
        },
        enableGlobalFilter: false,
      }
    ),

    // // Payment Status
    // columnHelper.accessor("payment_status", {
    //   header: () => <div className="th-text">Payment Status</div>,
    //   cell: info => <div className="text-sm">
    //     {info.getValue() || "—"}</div>,
    // }),
    // // Status
    // columnHelper.accessor("status", {
    //   header: () => <div className="th-text">Status</div>,
    //   cell: info => <div className="text-sm">{STATUS_MAP[info.getValue()] || "Unknown"}</div>,
    // }),
    // columnHelper.accessor(row => (row.priority !== undefined ? PRIORITY_LABELS[row.priority] : "—"), {
    //   id: "priority",
    //   header: () => <div className="th-text">Priority</div>,
    //   cell: info => <div className="text-sm">{info.getValue()}</div>,
    // }),
    // Actions
    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const order = info.row.original;
        return (
          <div className="d-flex  align-items-center gap-2">
            {canView && (
              <ViewIconButton
                className="btn action-btn fs-2 "
                to={`${baseRoute}/jobs/installationWorkOrder/${order._id}`}
                state={{ type: "View" }}
                title="View"
              />
            )}
            {
              (order.status === 600 || order.status === 400) ? (
                ""
              ) : order.status !== 500 ? (
                canEdit && (
                  <EditIconButton
                    className="icon-square me-3"
                    to={`${baseRoute}/jobs/installationWorkOrder/${order._id}`}
                    state={{ type: "Edit" }}
                    title="Edit"
                  />
                )
              ) : (
                canDelete && (
                  <DeleteIconButton
                    className="icon-square danger"
                    to={`${baseRoute}/jobs/addRemovalRequest/${order._id}`}
                    title="Removal Request"
                  />
                )
              )
            }
          </div>
        );
      }
    }),
  ], [pagination, baseRoute, canView, canEdit, canDelete]);


  // TanStack Table instance
  const table = useReactTable({
    data: orders,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const showTableLoader = loading && orders.length === 0;

  return (
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-3">
        {/* <h4 className="mb-0 fw-bold">Orders</h4> */}
        <h4></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/jobs/addInstallation`} className="btn btn-primary  table-addbutton">
            + Add Installation
          </Link>
          )}

        </div>
      </div>

      <div className="po-header mb-4">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2">
            <h4 className="mb-1 po-title">Installations</h4>
            {/* <div className="po-subtitle">Manage incoming products from suppliers and brokerages</div> */}
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by Order ID, Brokerage Firm, Agent"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setGlobalFilter(searchInput); }}
                autoComplete="off"
              />
            </div>

            {/* Filters toggle */}
            <button
              type="button"
              className={` po-filter-btn d-inline-flex align-items-center ${filtersOpen ? "active" : ""}`}
              onClick={() => setFiltersOpen(v => !v)}
              aria-expanded={filtersOpen}
              aria-controls="incoming-filters"
            >
              <i className="ri-filter-3-line me-2" />
              <span>Filters</span>
              <i className={`ri-arrow-down-s-line ms-1 ${filtersOpen ? "rotate-180" : ""}`} />
            </button>

            <button
              type="button"
              className="export-button"
              onClick={handleExportCsv}
              disabled={exporting}
            >
              <i className="ri-file-excel-2-line me-2" />
              {exporting ? "Exporting…" : "Export to Excel"}
            </button>

          </div>
        </div>
        <div id="incoming-filters" className={`po-filters ${filtersOpen ? "show" : ""}`}>
          <div className="po-filters-bar d-flex">
            <div className="row g-2 g-md-3 align-items-end justify-content-md-end ms-md-auto w-100 w-md-auto">

              {/* Date Range */}
              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1">Date Range</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={DATE_OPTIONS}
                    value={datePreset}
                    onChange={(val) => val && setDatePreset(val)}
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>

                {datePreset?.value === "custom" && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <div className="flex-fill">
                      <label className="form-label mb-1 label-1">From</label>
                      <input
                        type="date"
                        className="form-control"
                        min={minDate}
                        max={maxDate}
                        value={customFrom}
                        onChange={(e) => setCustomFrom(e.target.value)}
                      />
                    </div>
                    <div className="flex-fill">
                      <label className="form-label mb-1 label-1">To</label>
                      <input
                        type="date"
                        className="form-control"
                        min={minDate}
                        max={maxDate}
                        value={customTo}
                        onChange={(e) => setCustomTo(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Invoice Status */}
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Invoice Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={INVOICE_STATUS_OPTIONS}
                    value={invoiceSentFilter}
                    onChange={(option) => option && setInvoiceSentFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Order Status */}
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Order Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={orderStatusOptions}
                    value={orderStatusFilter}
                    onChange={(option) => option && setOrderStatusFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Payment Status */}
              <div className="col-12 col-lg-auto">
                <label className="form-label mb-1 label-1">Payment Status</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={PAYMENT_STATUS_OPTIONS}
                    value={paymentStatusFilter}
                    onChange={(option) => option && setPaymentStatusFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Priority */}
              <div className="col-12 col-md-auto">
                <label className="form-label mb-1 label-1">Priority</label>
                <div className="po-rs min-220">
                  <Select
                    classNamePrefix="rs"
                    options={PRIORITY_OPTIONS}
                    value={priorityFilter}
                    onChange={(option) => option && setPriorityFilter(option)}
                    isSearchable
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="col-12 col-md-auto d-flex justify-content-md-end gap-2 mt-1 mt-md-0">
                <button className="btn btn-primary table-addbutton flex-fill flex-lg-grow-0" onClick={fetchOrders}>
                  Apply Filters
                </button>

                <button
                  className="btn back-btn surface-btn fw-bold fs-16 flex-fill flex-lg-grow-0"
                  onClick={() => {
                    // Reset all filters to default values
                    setDatePreset(DATE_OPTIONS[3]); // "Last 30 Days" (default)
                    setCustomFrom("");
                    setCustomTo("");
                    setInvoiceSentFilter(INVOICE_STATUS_OPTIONS[0]);
                    setOrderStatusFilter(EMPTY_ORDER_STATUS_OPTION); // "Order Status" (default)
                    setPaymentStatusFilter(PAYMENT_STATUS_OPTIONS[0]); // "Payment Status" (default)
                    setPriorityFilter(PRIORITY_OPTIONS[0]); // "Priority" (default)
                    setGlobalFilter(""); // Clear search
                    setSearchInput(""); // Clear search input
                    setPagination({ pageIndex: 0, pageSize: 10 }); // Reset to first page
                    // Trigger fetch with cleared filters
                    fetchOrders();
                  }}
                >
                  Clear
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        <div className="align-items-center bg-white border-bottom d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 ">Installations</h5>
          <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
            {/* orders */}
          </span>

        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        <div className="rdt-desktop">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1280px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              {showTableLoader ? (
                <DesktopTableLoader table={table} message="Loading installations…" variant="dots" />
              ) : (
                <DesktopTableParts
                  table={table}
                  onRowClick={(row) => {
                    const orderId = getInstallationWorkOrderId(row);
                    if (!orderId) return;
                    navigate(`${baseRoute}/jobs/installationWorkOrder/${orderId}`);
                  }}
                />
              )}
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile ">
          {showTableLoader ? (
            <MobileCardsLoader message="Loading installations…" variant="dots" />
          ) : (
            <MobileCardsOnly
              table={table}
              idColId="order_link"
              titleColId="product_name"
              actionColId="actions"
              onRowClick={(row) => {
                const orderId = getInstallationWorkOrderId(row);
                if (!orderId) return;
                navigate(`${baseRoute}/jobs/installationWorkOrder/${orderId}`);
              }}
            />
          )}
        </div>
      </div>
      <DataTableStatus
        loading={showTableLoader}
        isEmpty={table.getRowModel().rows.length === 0}
      >
        <div className="mt-4 mb-4">
          <TablePaginationControls table={table} />
        </div>
      </DataTableStatus>
    </div>
  );
};

export default SignageJobsInstallations;
